import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Stepper, Step, StepLabel, Alert, CircularProgress, Paper } from '@mui/material';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { createClinic, createUser, resendVerification } from '../services/api';
import { Link as RouterLink } from 'react-router-dom';

const Register = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [clinicData, setClinicData] = useState({ name: '', address: '', phone: '' });
  const [userData, setUserData] = useState({ username: '', password: '', email: '' });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  // La cuenta se crea ANTES de intentar enviar el email de verificación —
  // si el envío falla (502), la cuenta ya existe en la DB pero el usuario
  // no tiene forma de saberlo ni de llegar al reenvío (que en Login.js solo
  // aparece después de un intento de login fallido). Este estado separa
  // ese caso puntual del resto de errores para ofrecer el reenvío acá mismo.
  const [emailFailed, setEmailFailed] = useState(false);
  const [resendState, setResendState] = useState('idle'); // 'idle' | 'sending' | 'sent'

  const steps = ['Datos de la Clínica', 'Datos de Usuario'];

  const handleClinicChange = (e) => setClinicData({ ...clinicData, [e.target.name]: e.target.value });

  const handleUserChange = (e) => setUserData({ ...userData, [e.target.name]: e.target.value });

  const handleNext = async () => {
    if (activeStep === 0) {
      if (!clinicData.name) {
        setError('El nombre de la clínica es obligatorio');
        return;
      }
      setError('');
      setActiveStep(1);
    } else {
      if (!userData.username || !userData.password) {
        setError('Usuario y contraseña son obligatorios');
        return;
      }
      setError(''); setFieldErrors({});
      setLoading(true);
      try {
        // 1. Crear clínica
        const clinicRes = await createClinic(clinicData);
        const clinicId = clinicRes.data.id;

        // 2. Crear usuario con clinic_id — dispara el email de confirmación.
        // Ya no hay login automático: la cuenta queda sin verificar hasta
        // que el usuario clickee el link que le llega por email.
        await createUser({ ...userData, clinic_id: clinicId });
        setRegistered(true);
      } catch (err) {
        // 502 = la cuenta y la clínica ya se crearon, solo falló el envío
        // del email — mostrar la salida de reenvío en vez de un error a secas.
        if (err.response?.status === 502 && userData.email) {
          setEmailFailed(true);
          setRegistered(true);
        } else {
          setFieldErrors(err.fieldErrors || {});
          setError(err.userMessage || 'Error al crear la cuenta. Revisá los datos e intentá de nuevo.');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleResend = async () => {
    setResendState('sending');
    try {
      await resendVerification(userData.email);
    } finally {
      setResendState('sent');
    }
  };

  const handleBack = () => setActiveStep(0);

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Nombre de la Clínica"
              name="name"
              value={clinicData.name}
              onChange={handleClinicChange}
              fullWidth
              required
              error={!clinicData.name && !!error}
              helperText={!clinicData.name ? error : ''}
            />
            <TextField
              label="Dirección"
              name="address"
              value={clinicData.address}
              onChange={handleClinicChange}
              fullWidth
            />
            <TextField
              label="Teléfono"
              name="phone"
              value={clinicData.phone}
              onChange={handleClinicChange}
              fullWidth
            />
          </Box>
        );
      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: '0.85rem' } }}>
              Este primer usuario queda como <strong>Admin</strong> — acceso total a la clínica. Si sos el único
              usuario (ej. médico dueño de su consultorio, que también hace de recepción), no hace falta crear
              otro usuario: con Admin alcanza para todo, vas a elegir tu propio nombre como médico en cada turno
              o consulta. Después podés crear más usuarios con roles limitados (Recepcionista, Médico) desde
              Configuración → Usuarios.
            </Alert>
            <TextField
              label="Usuario"
              name="username"
              value={userData.username}
              onChange={handleUserChange}
              fullWidth
              required
              error={!userData.username && !!error}
            />
            <TextField
              label="Contraseña"
              name="password"
              type="password"
              value={userData.password}
              onChange={handleUserChange}
              fullWidth
              required
              error={!userData.password && !!error}
              helperText="Mínimo 8 caracteres"
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={userData.email}
              onChange={handleUserChange}
              fullWidth
              error={!!fieldErrors.email}
              helperText={fieldErrors.email || "Recomendado: lo necesitás para confirmar tu cuenta y para recuperar la contraseña si la olvidás."}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  if (registered && emailFailed) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', p: 2 }}>
        <Paper sx={{ maxWidth: 440, width: '100%', p: 4, borderRadius: 2, textAlign: 'center' }}>
          <ErrorOutlineIcon color="warning" sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h6" fontWeight={700} mb={1}>Tu cuenta ya se creó</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {clinicData.name} y tu usuario <strong>{userData.username}</strong> quedaron registrados, pero no
            pudimos enviar el email de confirmación a <strong>{userData.email}</strong>. Sin confirmarlo no vas
            a poder iniciar sesión.
          </Typography>
          {resendState === 'sent' ? (
            <Alert severity="success" sx={{ mb: 2, textAlign: 'left' }}>
              Si el email es correcto, te reenviamos el link. Revisá tu casilla (y spam) en unos minutos.
            </Alert>
          ) : (
            <Button
              variant="contained" fullWidth sx={{ mb: 2 }}
              onClick={handleResend} disabled={resendState === 'sending'}
            >
              {resendState === 'sending' ? <CircularProgress size={20} color="inherit" /> : 'Reintentar envío del email'}
            </Button>
          )}
          <Button component={RouterLink} to="/login" variant="outlined" fullWidth>
            Ir a iniciar sesión
          </Button>
        </Paper>
      </Box>
    );
  }

  if (registered) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', p: 2 }}>
        <Paper sx={{ maxWidth: 440, width: '100%', p: 4, borderRadius: 2, textAlign: 'center' }}>
          {userData.email ? (
            <>
              <MarkEmailReadIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h6" fontWeight={700} mb={1}>Revisá tu email</Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Te enviamos un link de confirmación a <strong>{userData.email}</strong>. Hacé clic ahí para
                activar tu cuenta y empezar a usar {clinicData.name || 'la plataforma'}.
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                ¿No te llegó? Revisá spam, o intentá iniciar sesión — desde ahí vas a poder reenviarlo.
              </Typography>
            </>
          ) : (
            <>
              <MarkEmailReadIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h6" fontWeight={700} mb={1}>¡Cuenta creada!</Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Ya podés iniciar sesión en {clinicData.name || 'la plataforma'} con tu usuario y contraseña.
              </Typography>
            </>
          )}
          <Button component={RouterLink} to="/login" variant="contained" fullWidth>
            Ir a iniciar sesión
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, maxWidth: 500, mx: 'auto' }}>
      <Typography variant="h4" textAlign="center" gutterBottom>
        Registro en VitaNotas
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {getStepContent(activeStep)}

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
        {activeStep === 1 && (
          <Button onClick={handleBack} disabled={loading}>
            Atrás
          </Button>
        )}
        <Button variant="contained" onClick={handleNext} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : activeStep === 0 ? 'Siguiente' : 'Registrarme'}
        </Button>
      </Box>

      <Typography sx={{ mt: 2, textAlign: 'center' }}>
        ¿Ya tienes cuenta? <RouterLink to="/login">Iniciar sesión</RouterLink>
      </Typography>
    </Box>
  );
};

export default Register;